/**
 * A cuántos de un análisis les llega WhatsApp oficial, por qué no a los demás y qué hacer (textos en `resumenAlcance`).
 * Lo muestran el reporte y «Por dónde y cuándo» de Reactivación y la Audiencia del asistente oficial.
 *
 * Con 0 va en ámbar y ofrece el otro canal si quien lo usa lo pasa (`onOtroCanal`): la campaña oficial con 0 personas
 * recién fallaba al confirmar, después de la plantilla, las variables y la prueba.
 *
 * Una campaña de Nueva campaña con el consentimiento declarado después de prepararla ofrece «Preparar de nuevo» ahí
 * mismo (`onPrepararDeNuevo`, que pasa el asistente sólo al equipo: preparar es del equipo).
 */

import { useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, BadgeCheck, Loader2, RefreshCw } from "lucide-react";
import type { EstadoAlcance } from "../hooks/useAlcanceOficial";
import { resumenAlcance } from "../logic/alcance";

export function AlcanceOficial({ estado, onOtroCanal, otroCanalLabel = "Seguir por líneas y email", porQue, motor = false, onPrepararDeNuevo }: {
  estado: EstadoAlcance;
  /** la campaña se preparó en Nueva campaña (fase 4): el arreglo del consentimiento es declararlo, no rehacer el archivo */
  motor?: boolean;
  /**
   * Prepara de nuevo la segmentación y vuelve a contar; devuelve el error para mostrar, o null si salió. Sin esto, a
   * quien no es del equipo se le dice que se lo pida.
   */
  onPrepararDeNuevo?: (segmentacionId: string) => Promise<string | null>;
  onOtroCanal?: () => void;
  otroCanalLabel?: string;
  /** por qué cambia según el canal (`porQueDifieren`): lo pasa Reactivación, donde se elige entre los dos */
  porQue?: string | null;
}) {
  if (estado.cargando) {
    return (
      <p className="flex items-center gap-2 text-xs text-slate-500" aria-busy="true" data-testid="alcance-oficial-cargando">
        <Loader2 size={13} className="animate-spin" /> Contando a quiénes les llega por WhatsApp oficial…
      </p>
    );
  }
  // Sin la cuenta no se frena nada: el chequeo del backend sigue siendo la defensa.
  if (estado.error) {
    return <p className="text-xs text-slate-500" data-testid="alcance-oficial-error">No pudimos contar a quiénes les llega por WhatsApp oficial. {estado.error}</p>;
  }
  if (!estado.alcance) return null;
  const r = resumenAlcance(estado.alcance, { motor });
  const tono = r.ninguno ? "border-amber-200 bg-amber-50/70 text-amber-900" : "border-slate-200 bg-white text-slate-800";
  return (
    <div className={`rounded-xl border p-3.5 text-sm ${tono}`} role={r.ninguno ? "alert" : "status"} data-testid="alcance-oficial">
      <p className="flex items-start gap-2 font-semibold">
        {r.ninguno
          ? <AlertTriangle size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
          : <BadgeCheck size={16} className="text-violet-500 flex-shrink-0 mt-0.5" />}
        <span>{r.titulo}</span>
      </p>
      <div className={`mt-1.5 space-y-1 pl-6 ${r.ninguno ? "text-amber-800" : "text-slate-600"}`}>
        <p>{r.consentimiento}</p>
        {r.celular && <p>{r.celular}</p>}
        {r.fatiga && <p data-testid="alcance-oficial-fatiga">{r.fatiga}</p>}
        {r.exclusiones && <p data-testid="alcance-oficial-exclusiones">{r.exclusiones}</p>}
        {porQue && <p className="text-xs" data-testid="alcance-oficial-porque">{porQue}</p>}
        {!r.ninguno && <p className="text-xs text-slate-500">{r.nota}</p>}
      </div>
      {(r.arreglos.length > 0 || (r.ninguno && onOtroCanal)) && (
        <ul className="mt-2.5 space-y-1.5 pl-6 text-xs">
          {r.arreglos.map((a) => (
            <li key={a.ruta} className={r.ninguno ? "text-amber-800" : "text-slate-600"}>
              {a.texto}{" "}
              {!a.preparar ? (
                <Link to={a.ruta} className="font-semibold underline whitespace-nowrap">{a.label}</Link>
              ) : onPrepararDeNuevo ? (
                <PrepararDeNuevo segmentacionId={a.preparar} label={a.label} onPreparar={onPrepararDeNuevo} />
              ) : (
                <span className="font-semibold">Pedile al equipo de Deenex que la prepare de nuevo.</span>
              )}
            </li>
          ))}
          {r.ninguno && onOtroCanal && (
            <li>
              <button type="button" onClick={onOtroCanal}
                className="mt-1 h-8 px-3 rounded-lg border border-amber-300 bg-white text-xs font-semibold text-amber-900 hover:bg-amber-100">
                {otroCanalLabel}
              </button>
            </li>
          )}
        </ul>
      )}
    </div>
  );
}

function PrepararDeNuevo({ segmentacionId, label, onPreparar }: { segmentacionId: string; label: string; onPreparar: (id: string) => Promise<string | null> }) {
  const [preparando, setPreparando] = useState(false);
  const [error, setError] = useState("");
  const correr = async () => {
    if (preparando) return;
    setPreparando(true);
    setError("");
    const e = await onPreparar(segmentacionId);
    setPreparando(false);
    if (e) setError(e);
  };
  return (
    <span className="block mt-1.5">
      <button type="button" onClick={() => void correr()} disabled={preparando}
        className="h-8 px-3 rounded-lg bg-violet-600 text-xs font-semibold text-white hover:bg-violet-700 inline-flex items-center gap-1.5 disabled:opacity-50">
        {preparando ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} {preparando ? "Preparando…" : label}
      </button>
      {error && <span className="block mt-1 text-red-700" role="alert">{error}</span>}
    </span>
  );
}
